import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { PageHeader } from "@/components/page-header";
import { TeacherCards } from "@/components/teacher-chips";
import { pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(pages)/teachers/page.tsx.
 * SSR fetch in the original; here we use TanStack Query + Suspense.
 */
interface TeacherRecord {
  id: string;
  name: string;
  subjects: string[];
  qualifications?: string;
  batches: string[];
  login?: string;
}

function teachersQuery(authId: string | undefined) {
  return {
    queryKey: ["teachers", "list-page", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("teacher").getList<TeacherRecord>(1, 24, {
        skipTotal: true,
        fields: "id,name,subjects,qualifications,batches,login",
      }),
  };
}

export const Route = createFileRoute("/_app/teachers/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(teachersQuery(pb.authStore.model?.id)),
  component: TeachersPage,
});

function TeachersPage() {
  useDocumentTitle("Teachers");
  return (
    <Suspense fallback={null}>
      <Body />
    </Suspense>
  );
}

function Body() {
  const { data } = useSuspenseQuery(teachersQuery(pb.authStore.model?.id));
  return (
    <>
      <PageHeader eyebrow="Faculty" title="Teachers" meta={`${data.items.length} active`} />
      {data.items.length === 0 ? (
        <div className="border-border flex flex-col items-center justify-center rounded-2xl border border-dashed py-16 text-center">
          <p className="text-muted-foreground text-sm">No teachers found.</p>
        </div>
      ) : (
        <TeacherCards teachers={data.items} />
      )}
    </>
  );
}
